<template>
    <div>
        <h1 class="display-heading">Employee Spending Report (Panel Clinics)</h1>

        <div class="mb-3">
            <div class="row">
                <div class="col-4">
                    <datetime-picker ref="spendingReportDatePicker" class="form-control"></datetime-picker>
                </div>
                <div class="col">
                    <button class="btn btn-primary btn-primary-insv mr-2" @click="getCompanyEmployeeSpending">View</button>
                    <button class="btn btn-primary btn-primary-insv" @click="exportReport">Download Report</button>
                </div>
            </div>
        </div>

        <div ref="tableSection">
            <table ref="listTable" class="table table-hover">
                <colgroup>
                    <col width="40">
                    <col>
                    <col>
                    <col width="150">
                    <col width="200">
                    <col>
                    <col width="150">
                    <col width="150">
                    <col width="150">
                </colgroup>
                <thead>
                    <tr>
                        <th scope="col">#</th>
                        <th scope="col">Employee Name</th>
                        <th scope="col">Patient Name</th>
                        <th scope="col">Date</th>
                        <th scope="col">Payment Type</th>
                        <th scope="col">Payee Name</th>
                        <th scope="col" class="text-right">Company Credit</th>
                        <th scope="col" class="text-right">Self Paid</th>
                        <th scope="col" class="text-right">Total Paid</th>
                    </tr>
                </thead>

                <tbody>
                    <tr v-show="spendingList.length === 0">
                        <td :colspan="columnsCount" class="text-center font-italic">
                            {{isTableLoading ? "Loading Data": "No Data"}}
                        </td>
                    </tr>
                    <tr v-for="(historyItem, index) in spendingList" :key="index">
                        <th scope="row">{{((pagination.currentPage - 1) *pagination.pageSize ) + index + 1}}</th>
                        <td class="text-nowrap">{{historyItem.employeeName}}</td>
                        <td class="text-nowrap">{{historyItem.patientName}}</td>
                        <td class="text-nowrap">
                            {{historyItem.completedDate}}
                        </td>
                        <td class="text-nowrap">
                            {{historyItem.paymentType}}
                        </td>
                        <td class="text-nowrap">
                            {{historyItem.payeeName}}
                        </td>
                        <td class="text-nowrap text-right">
                            RM{{historyItem.companyCreditAmount}}
                        </td>
                        <td class="text-nowrap text-right">
                            RM{{historyItem.employeeAmount}}
                        </td>
                        <td class="text-nowrap text-right">
                            RM{{historyItem.paymentAmount}}
                        </td>
                    </tr>
                </tbody>
            </table>
            <pagination v-model="pagination.currentPage"
                :totalPages="pagination.totalPages"
                :scrollPosition="pagination.scrollPosition">
            </pagination>
        </div>
    </div>
</template>

<script>
import {
    apiPath
} from '@/config';
import moment from 'moment';
import datetimePicker from '@/components/datetime-picker';
import pagination from '@/components/pagination';
import {
    convertToUserReadableDate
} from '@/utilities';
import Cookies from 'js-cookie';
export default {
    'name': 'EmployeeSpendingReport',
    'components': {
        pagination,
        datetimePicker
    },
    mounted () {
        const pageContent = document.getElementsByClassName('page-content');
        pageContent[0].classList.add('wide-version');

        let today = moment().endOf('d');
        let last2Weeks = moment().add(-13, 'd').startOf('d');
        this.$refs.spendingReportDatePicker.setDate(last2Weeks, today);

        this.columnsCount = this.$refs.listTable.rows[0].cells.length;

        this.getCompanyEmployeeSpending();

        // initialize scroll position
        this.$nextTick(() => {
            this.pagination.scrollPosition = this.$refs.tableSection.getBoundingClientRect().top + window.scrollY - document.querySelector('.top-navigation').clientHeight;
        });
    },
    destroyed () {
        const pageContent = document.getElementsByClassName('page-content');
        pageContent[0].classList.remove('wide-version');
    },
    'computed': {
        currentPage () {
            return this.pagination.currentPage;
        },
        token () {
            return Cookies.get('t');
        }
    },
    'watch': {
        currentPage () {
            this.$nextTick(() => {
                this.getCompanyEmployeeSpending();
            });
        }
    },
    data () {
        return {
            'spendingList': [],
            'columnsCount': 1,
            'isTableLoading': true,
            'pagination': {
                'currentPage': 1,
                'totalPages': 0,
                'pageSize': 20,
                'scrollPosition': 0
            },
            'lastRequest': {
                'startDate': '',
                'endDate': ''
            }
        };
    },
    'methods': {
        getCompanyEmployeeSpending  () {
            let { startDate, endDate } = this.$refs.spendingReportDatePicker.getDates();

            let startDateIsoString = startDate.startOf('d').toISOString(true);
            let endDateIsoString = endDate.endOf('d').toISOString(true);

            this.spendingList = [];
            this.isTableLoading = true;

            this.axios.post('PaymentList/GetEmployeePayments', {
                'p': (startDateIsoString === this.lastRequest.startDate && endDateIsoString === this.lastRequest.endDate) ? this.pagination.currentPage : 1,
                'ps': this.pagination.pageSize,
                'sd': startDate.startOf('d').toISOString(true),
                'ed': endDate.endOf('d').toISOString(true)
            }).then(({
                data
            }) => {
                this.lastRequest.startDate = startDateIsoString;
                this.lastRequest.endDate = endDateIsoString;

                this.isTableLoading = false;
                let pagedResult = data.data;

                this.pagination.currentPage = pagedResult.p;
                this.pagination.totalPages = pagedResult.c;

                let list = pagedResult.l;

                this.spendingList = list.map((spending) => ({
                    'paymentSummaryId': spending.psid,
                    'paymentSummaryIdShort': spending.psids,
                    'paymentAmount': spending.am.toFixed(2),
                    'companyCreditAmount': spending.ca.toFixed(2),
                    'employeeAmount': spending.ea.toFixed(2),
                    'completedDate': spending.cd ? convertToUserReadableDate(spending.cd) : '-',
                    'employeeName': spending.en,
                    'patientName': spending.ptn,
                    'paymentType': this.getPaymentType(spending.pt),
                    'payeeName': spending.pn ? spending.pn : '-',
                    'externalShortId': spending.esid
                }));
            });
        },
        exportReport () {
            let { startDate, endDate } = this.$refs.spendingReportDatePicker.getDates();
            let startDateIsoString = startDate.startOf('d').format('YYYY-MM-DD');
            let endDateIsoString = endDate.endOf('d').format('YYYY-MM-DD');

            let url = `${apiPath}PaymentList/ExportEmployeePayments?startDate=${startDateIsoString}&endDate=${endDateIsoString}&token=${this.token}`;
            window.open(url, '_blank');
        },
        getPaymentType (paymentTypeId) {
            switch (paymentTypeId) {
            case 1:
                return 'Doctor Consultation';
            case 2:
                return 'Prescription Payment';
            case 4:
                return 'Clinic Treatment';
            case 6:
                return 'Clinic Medicine Purchase';
            default:
                return 'Other';
            }
        }
    }
};
</script>

<style>

</style>
